import React, { createContext, useCallback, useContext, useRef, useState } from 'react'
import { Box, Circle, Flex, Text } from '@chakra-ui/react'
import PropTypes from 'prop-types'
import { MdBarChart } from 'react-icons/md'
import rootStore from '../../../../store/RootStore'
import { CommentFilter } from '../CommentFilter'

import { scrollToReport } from '../../../../utils/MenuHelper'
import { observer } from 'mobx-react'
import { CommentList } from '../CommentList/CommentList'
import ConfirmAlertDialog from '../../../ui/ConfirmAlertDialog'
import { ReportContext } from '../../../../contexts/ReportContext'

/* eslint-disable @typescript-eslint/no-empty-function */
export const CommentCardContext = createContext({
  onEdit: () => { },
  onDelete: () => { },
})

const CommentCardGroup = observer(
  ({ index, chartName, chartId, chartFilter, comments, ...rest }) => {
    const { commentStore, authStore } = rootStore
    const [commentToDelete, setCommentToDelete] = useState()
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false)
    const { refScrollContainer } = useContext(ReportContext)
    const commentCardRef = useRef(null)

    const handleReply = useCallback(
      commentId => {
        commentStore.setTempComment({
          parentId: commentId,
          user: authStore.user,
        })
        commentStore.setIsFormVisible(true)
      },
      [commentStore, authStore.user],
    )

    const handleStartEditComment = useCallback(
      commentId => {
        const editComment = commentStore.chartComments.comments.find(
          comment => commentId === comment.id,
        )
        commentStore.setTempComment({
          ...editComment,
        })
        commentStore.setIsEditing(true)
        commentStore.setIsFormVisible(true)
      },
      [commentStore],
    )

    const handleStartDeleteComment = useCallback(commentId => {
      setConfirmDeleteVisible(true)
      setCommentToDelete(commentId)
    }, [])

    const handleConfirmDelete = useCallback(() => {
      commentStore.deleteComment(commentToDelete)
      setConfirmDeleteVisible(false)
      setCommentToDelete(-1)
    }, [commentStore, commentToDelete])

    const handleHeaderClick = useCallback(() => {
      scrollToReport(chartId, refScrollContainer.current)
    }, [chartId, refScrollContainer])

    const rootComments = comments?.filter(comment => !comment.parentId)

    return (
      <CommentCardContext.Provider
        value={{
          onEdit: handleStartEditComment,
          onDelete: handleStartDeleteComment,
          commentCardRef,
        }}
      >
        <Box
          border="1px solid"
          borderColor="brand.gray"
          borderRadius="8px"
          position="relative"
          boxShadow={`0 2px 12px 0 rgba(0,0,0,${index === 0 ? 0.19 : 0})`}
          left={index === 0 ? '-32px' : 0}
          {...rest}
          ref={commentCardRef}
        >
          <BadgeCommentsAmount amount={rootComments.length} />

          <CommentCardGroupHeader
            title={chartName}
            onClick={handleHeaderClick}
          />

          {chartFilter && <CommentFilter filters={chartFilter} />}
          {comments?.length > 0 ? (
            <CommentList
              comments={comments}
              onReply={handleReply}
            />
          ) : null}
        </Box>
        <ConfirmAlertDialog
          isOpen={confirmDeleteVisible}
          title="Delete comment"
          description="The comment will be deleted."
          onDismiss={() => setConfirmDeleteVisible(false)}
          onConfirm={handleConfirmDelete}
        />
      </CommentCardContext.Provider>
    )
  },
)

CommentCardGroup.propTypes = {
  index: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  chartName: PropTypes.string,
  chartFilter: PropTypes.array,
  comments: PropTypes.array,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  chartId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
}

export default React.memo(CommentCardGroup)

const CommentCardGroupHeader = ({ title, onClick }) => {
  return (
    <Flex
      flexDirection="row"
      bg="brand.lightGray"
      textStyle="tinyHeader"
      p="8px 32px 8px 16px"
      alignItems="center"
      borderTopRadius="8px"
      textOverflow="ellipses"
      onClick={onClick}
    >
      <Box mr="12px">
        <MdBarChart
          size="16px"
          color="brand.gray"
        />
      </Box>
      <Text
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        _hover={{
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {title}
      </Text>
    </Flex>
  )
}
CommentCardGroupHeader.propTypes = {
  title: PropTypes.string,
  onClick: PropTypes.func,
}

const BadgeCommentsAmount = ({ amount = 1 }) => {
  return (
    <Circle
      bg="orange"
      color="white"
      position="absolute"
      w="24px"
      h="24px"
      top={'4px'}
      left={'-12px'}
      zIndex={1}
      border="2px solid"
      borderColor="white"
      boxShadow={'0 2px 12px 0 rgba(0,0,0,0.19)'}
      fontSize={'12px'}
      fontWeight="bold"
    >
      {amount}
    </Circle>
  )
}

BadgeCommentsAmount.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
}
